import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle, X, CurrencyGbp, ShieldCheck, MapPin } from "@phosphor-icons/react";
import DeclineReasonModal from "../components/DeclineReasonModal";

export default function Messages() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [job, setJob] = useState(null);
  const [quote, setQuote] = useState(null);
  const [text, setText] = useState("");
  const [declineOpen, setDeclineOpen] = useState(false);
  const endRef = useRef(null);

  const loadConvs = async () => {
    try {
      const { data } = await api.get("/messages/conversations");
      setConvs(data || []);
      if (!active && data?.length) setActive(data[0]);
    } catch (e) {}
  };

  useEffect(() => { loadConvs(); }, []);

  const loadJobAndQuote = async (conv) => {
    setJob(null); setQuote(null);
    if (!conv?.job_id) return;
    try {
      const { data: j } = await api.get(`/jobs/${conv.job_id}`);
      setJob(j);
      // Find the quote between this customer and the other party
      const otherId = conv.other_user_id;
      const q = (j.quotes || []).find(qq => qq.tradesperson_id === otherId || qq.tradesperson_id === user?.user_id);
      setQuote(q || null);
    } catch {}
  };

  useEffect(() => {
    if (!active) return;
    api.get(`/messages/${active.conversation_id}`).then(({ data }) => {
      setThread(data || []);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    });
    loadJobAndQuote(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post("/messages", {
        recipient_id: active.other_user_id,
        job_id: active.job_id,
        body: text,
      });
      setText("");
      const { data } = await api.get(`/messages/${active.conversation_id}`);
      setThread(data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const acceptQuote = async () => {
    if (!quote) return;
    if (!window.confirm(`Accept ${quote.tradesperson_business || quote.tradesperson_name}'s quote of £${quote.amount.toFixed(2)}?`)) return;
    try {
      await api.post(`/quotes/${quote.quote_id}/accept`);
      toast.success("Quote accepted — job is now in progress");
      await loadJobAndQuote(active);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const declineQuote = async (body) => {
    if (!quote) return;
    try {
      await api.post(`/quotes/${quote.quote_id}/decline`, body || {});
      toast.success("Quote declined");
      setDeclineOpen(false);
      await loadJobAndQuote(active);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8" data-testid="messages-page">
        <h1 className="font-display font-extrabold text-3xl" style={{ color: "var(--brand-green)" }}>Messages</h1>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[60vh]">
          <aside className="brand-card overflow-hidden md:col-span-1" data-testid="conversation-list">
            {convs.length === 0 && <p className="p-5 text-sm text-[var(--text-muted)]">No conversations yet.</p>}
            {convs.map((c) => (
              <button
                key={c.conversation_id}
                onClick={() => setActive(c)}
                className={`w-full text-left p-4 border-b border-[var(--border-stone)] last:border-b-0 ${active?.conversation_id===c.conversation_id ? "bg-[#FAF8F2]" : "hover:bg-[#FAF8F2]"}`}
                data-testid={`conv-${c.conversation_id}`}
              >
                <div className="flex justify-between items-center gap-2">
                  <span className="font-display font-semibold text-[var(--brand-green)] truncate">{c.other_user_name}</span>
                  {c.unread > 0 && <span className="text-[10px] font-bold bg-[var(--brand-orange)] text-white px-1.5 py-0.5 rounded-full">{c.unread}</span>}
                </div>
                <div className="text-xs text-[var(--text-muted)] truncate">{c.last_message}</div>
              </button>
            ))}
          </aside>
          <section className="brand-card md:col-span-2 flex flex-col" data-testid="conversation-pane">
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-[var(--text-muted)] p-10">Select a conversation</div>
            ) : (
              <>
                <header className="p-4 border-b border-[var(--border-stone)]">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-display font-semibold text-[var(--brand-green)]">{active.other_user_name}</div>
                      {job && (
                        <Link to={`/jobs/${job.job_id}`} className="text-xs text-[var(--text-muted)] hover:text-[var(--brand-green)]" data-testid="conv-job-link">
                          About: {job.title} · {job.postcode}
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Quote banner — visible to job owner if there's a quote between them and the other party */}
                  {quote && job && user?.user_id === job.customer_id && (
                    <div className="mt-3 rounded-lg p-4 border" style={{ borderColor: "var(--brand-orange)", background: "rgba(226,109,41,0.06)" }} data-testid="quote-banner">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="text-xs font-semibold text-[var(--brand-orange)] uppercase tracking-wide">Quote from {quote.tradesperson_business || quote.tradesperson_name}</div>
                          <div className="font-display font-extrabold text-2xl text-[var(--brand-green)] flex items-center gap-2 mt-1">
                            <CurrencyGbp size={20} weight="bold" /> {quote.amount.toFixed(2)}
                            {quote.tradesperson_verified && <ShieldCheck size={16} weight="fill" className="text-[var(--brand-green)]" />}
                            {typeof quote.distance_miles === "number" && (
                              <span className="text-xs font-semibold text-[var(--brand-orange)] inline-flex items-center gap-1"><MapPin size={12} weight="fill" /> {quote.distance_miles} mi</span>
                            )}
                          </div>
                          <p className="text-sm text-[var(--text-main)] mt-1.5 whitespace-pre-wrap">{quote.message}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-0.5 rounded-md text-xs font-semibold uppercase tracking-wide ${
                            quote.status === "accepted" ? "bg-emerald-100 text-emerald-700" :
                            quote.status === "declined" ? "bg-red-100 text-red-700" :
                            "bg-[rgba(226,109,41,0.15)] text-[var(--brand-orange)]"
                          }`} data-testid="quote-banner-status">{quote.status}</span>
                        </div>
                      </div>
                      {quote.status === "pending" && job.status === "open" && (
                        <div className="mt-3 flex gap-2 flex-wrap">
                          <button onClick={acceptQuote} className="btn-primary text-sm" data-testid="msg-accept-quote-btn">
                            <CheckCircle size={16} weight="fill" /> Accept £{quote.amount.toFixed(2)}
                          </button>
                          <button onClick={() => setDeclineOpen(true)} className="btn-ghost text-sm" data-testid="msg-decline-quote-btn">
                            <X size={16} /> Decline
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </header>
                <div className="flex-1 overflow-y-auto p-4 space-y-2 scrollbar-thin" style={{ maxHeight: "55vh" }}>
                  {thread.map((m) => {
                    const mine = m.sender_id === user?.user_id;
                    return (
                      <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] px-3 py-2 rounded-lg text-sm ${mine ? "bg-[var(--brand-green)] text-white" : "bg-[#F1EFE8] text-[var(--text-main)]"}`}>
                          {m.body}
                          <div className={`mt-1 text-[10px] ${mine ? "text-white/70" : "text-[var(--text-muted)]"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
                <form onSubmit={send} className="border-t border-[var(--border-stone)] p-3 flex gap-2">
                  <input
                    data-testid="message-input"
                    value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="Write a message…"
                    className="flex-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]"
                  />
                  <button className="btn-primary" data-testid="message-send"><PaperPlaneTilt size={16} weight="fill" /></button>
                </form>
              </>
            )}
          </section>
        </div>
      </div>

      <DeclineReasonModal
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        onConfirm={declineQuote}
        traderName={quote?.tradesperson_business || quote?.tradesperson_name}
        amount={quote?.amount}
      />
    </Layout>
  );
}
